import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Save, Loader2, ChevronDown } from 'lucide-react';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import Header from '../components/Header';

export default function DisplaySettingsPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { preferences, updatePreferences } = useUserPreferences();

    const [currency, setCurrency] = useState(preferences.currency);
    const [thousandSeparator, setThousandSeparator] = useState(preferences.thousandSeparator);
    const [language, setLanguage] = useState(preferences.language || i18n.language);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const currencies = [
        { code: 'IDR', label: 'Indonesian Rupiah (Rp)' },
        { code: 'USD', label: 'US Dollar ($)' },
        { code: 'EUR', label: 'Euro (€)' },
        { code: 'GBP', label: 'British Pound (£)' },
        { code: 'JPY', label: 'Japanese Yen (¥)' },
        { code: 'SGD', label: 'Singapore Dollar (S$)' },
        { code: 'MYR', label: 'Malaysian Ringgit (RM)' },
        { code: 'INR', label: 'Indian Rupee (₹)' },
    ];

    const separators = [
        { value: '.', label: 'Titik (1.234.567)' },
        { value: ',', label: 'Koma (1,234,567)' },
    ];

    const handleSave = async () => {
        setLoading(true);
        setSuccess(false);

        try {
            await updatePreferences(currency, thousandSeparator, language);
            // Change i18n language
            i18n.changeLanguage(language);
            setSuccess(true);

            // Navigate back after a short delay
            setTimeout(() => {
                navigate('/profile');
            }, 1000);
        } catch (error) {
            console.error('Failed to update preferences:', error);
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/profile');
    };

    // Check if there are changes
    const hasChanges = currency !== preferences.currency || 
                       thousandSeparator !== preferences.thousandSeparator ||
                       language !== (preferences.language || i18n.language);

    return (
        <div
            className="min-h-screen bg-[#ffffeb]"
            style={{
                backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
            }}
        >
            {/* Header */}
            <Header
                leftContent={
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleCancel}
                            className="lg:hidden p-2 hover:bg-[#2da877] border-2 border-[#077f5d]"
                            style={{
                                boxShadow: '2px 2px 0 #077f5d',
                                transition: 'none'
                            }}
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <p
                            className="text-left text-base font-bold uppercase"
                            style={{
                                fontFamily: "'Press Start 2P', monospace",
                                fontSize: '14px',
                            }}
                        >
                            {t('display_settings.title')}
                        </p>
                    </div>
                }
                maxWidth="max-w-lg"
                hideProfileMenu
            />

            {/* Content */}
            <div className="max-w-lg mx-auto px-6 py-6 pb-24 lg:pb-8">
                {/* Success Message */}
                {success && (
                    <div
                        className="mb-4 bg-[#dffbd0] border-2 border-[#077f5d] text-[#077f5d] px-4 py-3 font-mono"
                        style={{ boxShadow: '3px 3px 0 #077f5d' }}
                    >
                        <p className="text-sm font-bold">✓ {t('display_settings.save_success')}</p>
                        <p className="text-xs mt-1">{t('display_settings.save_redirect')}</p>
                    </div>
                )}

                <div className="space-y-4">
                    {/* Language Selection */}
                    <div className="bg-white border-2 border-[#077f5d] p-4" style={{ boxShadow: '3px 3px 0 #077f5d' }}>
                        <label
                            className="block text-[#077f5d] mb-3 uppercase"
                            style={{
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: '10px',
                                fontWeight: 700
                            }}
                        >
                            {t('display_settings.language_section')}
                        </label>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setLanguage('en')}
                                disabled={loading}
                                className={`flex-1 py-3 px-4 border-2 font-mono font-bold text-sm ${
                                    language === 'en'
                                        ? 'bg-[#3cbe8c] text-white border-[#077f5d]'
                                        : 'bg-white text-[#1a1a1a] border-[#077f5d] hover:bg-[#dffbd0]'
                                }`}
                                style={{
                                    boxShadow: language === 'en' ? '2px 2px 0 #077f5d' : '2px 2px 0 #077f5d',
                                    transition: 'none'
                                }}
                            >
                                🇺🇸 {t('display_settings.lang_en')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setLanguage('id')}
                                disabled={loading}
                                className={`flex-1 py-3 px-4 border-2 font-mono font-bold text-sm ${
                                    language === 'id'
                                        ? 'bg-[#3cbe8c] text-white border-[#077f5d]'
                                        : 'bg-white text-[#1a1a1a] border-[#077f5d] hover:bg-[#dffbd0]'
                                }`}
                                style={{
                                    boxShadow: language === 'id' ? '2px 2px 0 #077f5d' : '2px 2px 0 #077f5d',
                                    transition: 'none'
                                }}
                            >
                                🇮🇩 {t('display_settings.lang_id')}
                            </button>
                        </div>
                    </div>

                    {/* Combined Currency & Separator Selection */}
                    <div className="bg-white border-2 border-[#077f5d] p-4" style={{ boxShadow: '3px 3px 0 #077f5d' }}>
                        <label
                            className="block text-[#077f5d] mb-3 uppercase"
                            style={{
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: '10px',
                                fontWeight: 700
                            }}
                        >
                            {t('display_settings.currency_section')}
                        </label>

                        {/* Combined Display */}
                        <div className="flex gap-2">
                            {/* Currency Symbol Dropdown */}
                            <div className="relative flex-shrink-0">
                                <select
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                    className="appearance-none px-6 py-4 pr-10 border-2 border-[#077f5d] bg-white font-mono font-bold text-lg hover:bg-[#dffbd0] cursor-pointer"
                                    style={{
                                        boxShadow: '2px 2px 0 #077f5d',
                                        transition: 'none'
                                    }}
                                    disabled={loading}
                                >
                                    {currencies.map(c => (
                                        <option key={c.code} value={c.code}>
                                            {(() => {
                                                const symbols: Record<string, string> = {
                                                    'IDR': 'Rp',
                                                    'USD': '$',
                                                    'EUR': '€',
                                                    'GBP': '£',
                                                    'JPY': '¥',
                                                    'SGD': 'S$',
                                                    'MYR': 'RM',
                                                    'INR': '₹'
                                                };
                                                return symbols[c.code] || c.code;
                                            })()}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#077f5d] pointer-events-none" />
                            </div>

                            {/* Formatted Amount Dropdown */}
                            <div className="relative flex-1">
                                <select
                                    value={thousandSeparator}
                                    onChange={(e) => setThousandSeparator(e.target.value)}
                                    className="appearance-none w-full px-6 py-4 pr-10 border-2 border-[#077f5d] bg-white font-mono font-bold text-lg hover:bg-[#dffbd0] text-right cursor-pointer"
                                    style={{
                                        boxShadow: '2px 2px 0 #077f5d',
                                        transition: 'none'
                                    }}
                                    disabled={loading}
                                >
                                    {separators.map(s => (
                                        <option key={s.value} value={s.value}>
                                            {(() => {
                                                const n = 12345678;
                                                const locale = s.value === '.' ? 'id-ID' : 'en-US';
                                                return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
                                            })()}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#077f5d] pointer-events-none" />
                            </div>
                        </div>

                        <p className="mt-3 text-xs text-[#077f5d] font-mono">
                            {t('display_settings.currency_hint')}
                        </p>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 space-y-3">
                    <button
                        onClick={handleSave}
                        disabled={loading || !hasChanges}
                        className="w-full bg-[#3cbe8c] text-white font-bold py-3 px-6 border-2 border-[#077f5d] hover:bg-[#2da877] disabled:opacity-50 disabled:cursor-not-allowed uppercase flex items-center justify-center gap-2"
                        style={{
                            boxShadow: '4px 4px 0 #077f5d',
                            transition: 'none',
                            fontFamily: "'IBM Plex Mono', monospace"
                        }}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                {t('display_settings.saving')}
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                {t('display_settings.save')}
                            </>
                        )}
                    </button>

                    <button
                        onClick={handleCancel}
                        disabled={loading}
                        className="w-full bg-white text-[#1a1a1a] font-bold py-3 px-6 border-2 border-[#077f5d] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                            boxShadow: '4px 4px 0 #077f5d',
                            transition: 'none',
                            fontFamily: "'IBM Plex Mono', monospace"
                        }}
                    >
                        {t('display_settings.cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
}
